import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { BANK_ACCOUNT_REQUEST_LIST_PATH } from './bank-account-request-list.screen';
import type { BankAccountRequest } from './bank-account-request.wire';

// Manifesto do detalhe (U23/U42). As ferramentas com efeito ficam presas à solicitação da rota (o padrão
// de `wallet-agent.ts`): a entrada não tem `id` nem alvo livre; fora do detalhe, o `run` recusa, e a
// tela troca `run` e `preview` pelos do registro carregado, só quando a linha está visível na barra.
// Excluir também fica aqui, preso à solicitação aberta (B1): na lista, o modelo escolheria a linha.
// Ficam de fora do agente: assinatura (U24), consulta paga (X01), step-up, atribuir e compartilhar (dão
// acesso ao registro), definir status, cancelar, reprovar, remover restrições, abrir conta e o salvar.

/** Abas do legado, na mesma ordem (U44; BankAccountRequestForm.tsx:61-70). As condicionais saem por `hidden`. */
export const BANK_ACCOUNT_REQUEST_TABS = [
  { id: 'informacoes', label: msg`Informações` },
  { id: 'envolvidos', label: msg`Envolvidos` },
  { id: 'documentos', label: msg`Documentos` },
  { id: 'acessos-concedidos', label: msg`Acessos concedidos` },
  { id: 'kyc', label: msg`KYC` },
  { id: 'historico', label: msg`Histórico` },
] as const;
export type BankAccountRequestTab = (typeof BANK_ACCOUNT_REQUEST_TABS)[number]['id'];
const TAB_IDS = BANK_ACCOUNT_REQUEST_TABS.map((tab) => tab.id) as [BankAccountRequestTab, ...BankAccountRequestTab[]];

/** `?tab=` só aceita as abas do manifesto; a primeira não vai para a URL (U42). `sheet` é do sheet de item. */
export const bankAccountRequestDetailSearch = z.object({ tab: z.enum(TAB_IDS).optional().catch(undefined), sheet: z.string().optional().catch(undefined) });

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
export const OPEN_REQUEST_ONLY = 'This action only runs on the bank account request detail screen, on the request that is open there.';
const needsOpenRequest = () => {
  throw new Error(OPEN_REQUEST_ONLY);
};

export const bankAccountRequestRefresh = action({
  id: 'bankAccountRequest.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a solicitação de conta aberta. Nada é alterado.`,
  run: () => undefined,
});

const tabList = TAB_IDS.join(', ');

export const bankAccountRequestOpenTab = action<{ tab: BankAccountRequestTab }>({
  id: 'bankAccountRequest.openTab',
  kind: 'navigate',
  label: msg`Abrir aba`,
  description: msg`Troca a aba da solicitação de conta aberta. Abas: ${tabList}.`,
  input: z.object({ tab: z.enum(TAB_IDS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab }),
});

export const emptyInput = z.strictObject({});
export const optionalMessageInput = z.strictObject({ message: z.string().max(500).optional() });
export const requiredMessageInput = z.strictObject({ message: z.string().trim().min(1).max(500) });
export const contractInput = z.strictObject({ useWatermark: z.boolean().optional(), createPdf: z.boolean().optional() });

export const bankAccountRequestSubmitApproval = action({
  id: 'bankAccountRequest.submitApproval',
  kind: 'write',
  label: msg`Enviar para aprovação`,
  description: msg`Envia para aprovação a solicitação de conta aberta na tela (em Draft, Timeout, Cancel, CorbanPreAnalysis ou Revision). Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  input: emptyInput,
  preview: () => ({ title: i18n._(msg`Enviar para aprovação?`) }),
  run: needsOpenRequest,
});

export const bankAccountRequestApprove = action<z.infer<typeof optionalMessageInput>>({
  id: 'bankAccountRequest.approve',
  kind: 'write',
  label: msg`Aprovar`,
  description: msg`Aprova a solicitação de conta aberta na tela, em Approval, com uma observação opcional. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  input: optionalMessageInput,
  preview: () => ({ title: i18n._(msg`Aprovar solicitação`) }),
  run: needsOpenRequest,
});

export const bankAccountRequestApproveInstrument = action<z.infer<typeof optionalMessageInput>>({
  id: 'bankAccountRequest.approveInstrument',
  kind: 'write',
  label: msg`Aprovar instrumento`,
  description: msg`Aprova o instrumento da solicitação de conta aberta na tela, em InstrumentApproval, com uma mensagem opcional. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  permission: can('BankAccountRequest', 'InstrumentApproval'),
  input: optionalMessageInput,
  preview: () => ({ title: i18n._(msg`Aprovar instrumento`) }),
  run: needsOpenRequest,
});

export const bankAccountRequestRejectInstrument = action<z.infer<typeof requiredMessageInput>>({
  id: 'bankAccountRequest.rejectInstrument',
  kind: 'destructive',
  label: msg`Reprovar instrumento`,
  description: msg`Reprova o instrumento da solicitação de conta aberta na tela, em InstrumentApproval, com uma mensagem obrigatória. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  permission: can('BankAccountRequest', 'InstrumentApproval'),
  input: requiredMessageInput,
  preview: () => ({ title: i18n._(msg`Rejeitar instrumento`), tone: 'destructive' }),
  run: needsOpenRequest,
});

export const bankAccountRequestDoneSignatures = action({
  id: 'bankAccountRequest.doneSignatures',
  kind: 'write',
  label: msg`Concluir assinaturas`,
  description: msg`Conclui a etapa de assinaturas da solicitação de conta aberta na tela, em Signatures. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  permission: can('BankAccountRequest', 'Update'),
  input: emptyInput,
  preview: () => ({ title: i18n._(msg`Concluir assinatura`) }),
  run: needsOpenRequest,
});

export const bankAccountRequestOpeningReview = action<z.infer<typeof requiredMessageInput>>({
  id: 'bankAccountRequest.openingReview',
  kind: 'write',
  label: msg`Concluir revisão`,
  description: msg`Conclui a revisão de abertura de conta da solicitação aberta na tela, em OpeningAccountRevision, com uma justificativa obrigatória. Só no correspondente raiz da UY3, com OpenBankAccount. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  permission: can('BankAccountRequest', 'OpenBankAccount'),
  input: requiredMessageInput,
  preview: () => ({ title: i18n._(msg`Concluir revisão de abertura de conta`) }),
  run: needsOpenRequest,
});

export const bankAccountRequestGenerateContract = action<z.infer<typeof contractInput>>({
  id: 'bankAccountRequest.generateContract',
  kind: 'write',
  label: msg`Gerar contrato`,
  description: msg`Gera a minuta do contrato da solicitação de conta aberta na tela (em Draft ou Revision), com marca d'água e em PDF se pedido; ela aparece na aba Documentos. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  input: contractInput,
  preview: () => ({ title: i18n._(msg`Gerar contrato`) }),
  run: needsOpenRequest,
});

/** Excluir da barra (ButtonsOfActions.tsx:280-288): só a solicitação aberta; a lista não oferece ao agente (B1). */
export const bankAccountRequestDetailDelete = action({
  id: 'bankAccountRequest.delete',
  kind: 'destructive',
  label: msg`Excluir`,
  description: msg`Exclui a solicitação de conta aberta na tela (em Draft, Revision ou CorbanPreAnalysis) e volta para a lista. Não recebe id: a solicitação é sempre a aberta na tela e não pode ser escolhida; para agir sobre outra, abra-a antes.`,
  permission: can('BankAccountRequest', 'Delete'),
  input: emptyInput,
  preview: () => ({ title: i18n._(msg`Excluir solicitação`), tone: 'destructive' }),
  run: needsOpenRequest,
});

export const bankAccountRequestDetailScreen = defineScreen({
  id: 'bankAccountRequest.detail',
  path: `${BANK_ACCOUNT_REQUEST_LIST_PATH}/$bankAccountId`,
  title: msg`Solicitação de conta`,
  description: msg`Cria ou altera uma solicitação de abertura de conta: titular, produto e motivo, envolvidos, documentos, acessos concedidos, KYC e histórico, com a barra de ações que muda conforme o status (aprovação, instrumento, assinaturas, abertura da conta).`,
  permission: can('BankAccountRequest', 'Read'),
  place: { icon: 'file-text', parent: 'bankAccountRequest.list', record: msg`Solicitação`, tabs: BANK_ACCOUNT_REQUEST_TABS },
  actions: [
    bankAccountRequestRefresh,
    bankAccountRequestOpenTab,
    bankAccountRequestSubmitApproval,
    bankAccountRequestApprove,
    bankAccountRequestApproveInstrument,
    bankAccountRequestRejectInstrument,
    bankAccountRequestDoneSignatures,
    bankAccountRequestOpeningReview,
    bankAccountRequestGenerateContract,
    bankAccountRequestDetailDelete,
  ],
});

/** O que o agente lê do detalhe (U24, U46 D2-C): no idioma da tela, status, produto, aba e ações; nome e documento ficam de fora. */
export function bankAccountRequestDetailContext(record: BankAccountRequest | undefined, visible: readonly string[], tab: string): ScreenContext {
  if (!record) return { summary: t`Formulário de nova solicitação de conta, em branco.`, sensitivity: 'restricted' };
  const status = record.status;
  const statusLabel = record.statusDisplay ?? status;
  const product = record.bankAccountProductIdDisplay ?? '-';
  const type = record.type ?? '-';
  const tabTitle = BANK_ACCOUNT_REQUEST_TABS.find((item) => item.id === tab)?.label;
  const tabLabel = tabTitle ? i18n._(tabTitle) : tab;
  const actions = visible.join(', ');
  return {
    summary: [
      t`Solicitação de conta no status ${statusLabel} (${status}), produto ${product}, tipo de conta ${type}, aba ${tabLabel}.`,
      visible.length > 0 ? t`Ações disponíveis: ${actions}.` : t`Nenhuma ação disponível.`,
    ].join(' '),
    entity: { type: 'BankAccountRequest', id: record.id },
    fields: { status: record.status, product: record.bankAccountProductIdDisplay ?? null, type: record.type ?? null, step: record.stepDisplay ?? null },
    sensitivity: 'restricted',
  };
}
